import type { ReactNode } from "react";
import clsx from "clsx";

import { Icon, ICON_TYPE } from "@/components/library/Icon";

// Pairings keep the initial at WCAG AA contrast, unlike the mock's mid/light blues.
const PRESENTER_AVATAR_STYLES = [
  "bg-gail-primary text-white",
  "bg-gail-primary-light text-gail-navy",
  "bg-gail-navy text-white",
];

export function SchedulerTabs({
  tabs,
}: Readonly<{ tabs: Array<{ label: string }> }>) {
  if (tabs.length === 0) {
    return null;
  }

  return (
    <ul className="flex items-center gap-5 md:gap-7">
      {tabs.map((tab, index) => (
        <li
          key={`${tab.label}-${index}`}
          className={clsx(
            "py-4 font-mono text-[11px] uppercase tracking-[0.18em]",
            index === 0
              ? "-mb-px border-b-2 border-gail-primary font-semibold text-gail-primary"
              : "text-gail-gray",
          )}
        >
          {tab.label}
        </li>
      ))}
    </ul>
  );
}

export function SchedulerCardFrame({
  tabs = [],
  poweredByLabel,
  children,
}: Readonly<{
  tabs?: Array<{ label: string }>;
  poweredByLabel?: string;
  children: ReactNode;
}>) {
  const hasHeader = tabs.length > 0 || Boolean(poweredByLabel);

  return (
    <div className="overflow-hidden rounded-2xl border border-black/10 bg-white shadow-[0_30px_70px_-40px_rgba(0,0,0,0.45)]">
      {hasHeader && (
        <div className="flex flex-wrap items-center justify-between gap-x-6 border-b border-black/5 bg-marketing-surface px-5 md:px-7">
          <SchedulerTabs tabs={tabs} />
          {poweredByLabel && (
            <span className="py-4 font-mono text-[11px] uppercase tracking-[0.18em] text-gail-gray">
              {poweredByLabel}
            </span>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

export function DurationBadge({ label }: Readonly<{ label: string }>) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-gail-primary-light px-4 py-2 font-monument text-sm font-semibold text-gail-primary">
      <Icon icon="clock" type={ICON_TYPE.REGULAR} className="h-3.5 w-3.5 shrink-0" />
      {label}
    </span>
  );
}

export function PresenterAvatars({
  label,
  names,
  presenters,
}: Readonly<{
  label?: string;
  names?: string;
  presenters: Array<{ name: string }>;
}>) {
  const hasDescriptiveText = Boolean(label) || Boolean(names);

  return (
    <div className="flex items-center gap-4">
      {presenters.length > 0 && (
        <div
          className="flex -space-x-2"
          aria-hidden={hasDescriptiveText || undefined}
        >
          {presenters.map((presenter, index) => (
            <span
              key={`${presenter.name}-${index}`}
              className={clsx(
                "flex h-9 w-9 items-center justify-center rounded-full ring-2 ring-white font-monument text-sm font-semibold",
                PRESENTER_AVATAR_STYLES[index % PRESENTER_AVATAR_STYLES.length],
              )}
            >
              <span aria-hidden="true">
                {presenter.name.trim().charAt(0).toUpperCase()}
              </span>
              {!hasDescriptiveText && (
                <span className="sr-only">{presenter.name}</span>
              )}
            </span>
          ))}
        </div>
      )}
      <div>
        {label && (
          <p className="font-monument text-sm font-semibold text-black">
            {label}
          </p>
        )}
        {names && (
          <p className="font-monument text-sm text-gail-gray">{names}</p>
        )}
      </div>
    </div>
  );
}
